import StudioAPI from "../api/StudioAPI";
import {
  deleteSessionBegin, deleteSessionFailure, deleteSessionSuccess, fetchSessionBegin, fetchSessionFailure,
  fetchSessionSuccess,
  postSessionBegin, postSessionError,
  postSessionSuccess, updateMessage, clearMessage, showTwoFaSetupDialog,
  postOtpBegin, postOtpSuccess, postOtpFailure,
  resetSubmitState, setOtpToken
} from "../actions/login";
import {GenericThunkDispatch, GenericThunkGetState, LogLevel, RecaptchaWindow} from "../types/Types";
import {OtpRequiredData, SessionErrorResponse} from "../types/StudioAPITypes";
import {ROUTES, TWO_FACTOR_SETUP_REQUIRED, OTP_REQUIRED} from "../utils/constants";
import history from "../utils/history";
import {logout} from "../utils/routerUtils";

const api = StudioAPI.getInstance();

export const postSession = (username: string, password: string) => {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    const recaptchaWindow = <RecaptchaWindow>window;
    const state = getState();
    let recaptchaResponse: string | null = null;
    if (state.login.showRecaptcha) {
      recaptchaResponse = recaptchaWindow.grecaptcha.getResponse(state.login.recaptcha);
      if (recaptchaResponse === "") {
        const response: SessionErrorResponse = {
          code: 401,
          data: {},
          error: [],
          message: "Please verify you are not a robot before signing in.",
          messageid: "",
          metadata: {},
          status: 401,
          statustext: "",
          version: 1
        }

        dispatch(postSessionError(response, state.login.failedAttempts, true));
        return;
      }
    }

    dispatch(postSessionBegin());
    api.postSession({
      username: username,
      password: password,
      recaptcha: recaptchaResponse
    })
      .then(response => {
        // Check if OTP/2FA is required
        if (response.messageid === OTP_REQUIRED) {
          const { otpToken } = response.data as OtpRequiredData;
          sessionStorage.setItem('otpToken', otpToken);
          dispatch(setOtpToken(otpToken));
          dispatch(resetSubmitState());
          history.push(ROUTES.TWO_FACTOR_AUTH);
          return;
        }
          const targetUrl = state.login.targetUrl;
          const isDeepLink = targetUrl &&
            targetUrl !== ROUTES.AUTH &&
            !targetUrl.startsWith(ROUTES.TWO_FACTOR_AUTH);

          if (isDeepLink) {
            if (targetUrl.startsWith('/projects') || targetUrl.startsWith('/error')) {
              history.replace(targetUrl);
            } else {
              window.location.replace(window.location.origin + '/#' + targetUrl);
            }
          } else {
            history.replace(ROUTES.PROJECTS);
          }

          dispatch(postSessionSuccess(response.data));
        },
        (errorResponse: SessionErrorResponse) => {
          const state = getState();
          let failedLoginAttempts = state.login.failedAttempts;
          let showRecaptcha = state.login.showRecaptcha;
          if (showRecaptcha) {
            recaptchaWindow.grecaptcha.reset(state.login.recaptcha);
          }
          let errorMessage = "";
          if (errorResponse.code === 401) {
            // If the response data has the number of failed attempts for the user and that number is
            // greater than the number of failed attempts for this session then use that number.
            // Otherwise just increment the local counter
            if (errorResponse.data !== undefined && errorResponse.data.failedLoginAttempts !== undefined) {
              if (state.login.failedAttempts < errorResponse.data.failedLoginAttempts) {
                failedLoginAttempts = errorResponse.data.failedLoginAttempts;
              }
              else {
                failedLoginAttempts++;
              }

              showRecaptcha = !!errorResponse.data.showRecaptchaAttempts && failedLoginAttempts >= errorResponse.data.showRecaptchaAttempts;
              errorMessage = "Incorrect username or password."
            }
          } else if (errorResponse.code === 403 && errorResponse.messageid === TWO_FACTOR_SETUP_REQUIRED && errorResponse.data?.setupUrl) {
            dispatch(showTwoFaSetupDialog(errorResponse.data.setupUrl));
            dispatch(postSessionError(errorResponse, failedLoginAttempts, showRecaptcha));
            return;
          } else if (!errorMessage) {
            errorMessage = "An unknown error occurred."
          }

          if (errorMessage) {
            dispatch(updateMessage(errorMessage, LogLevel.Error));
          }

          dispatch(postSessionError(errorResponse, failedLoginAttempts, showRecaptcha))
        }
      )
  }
}

export const postOtp = (otpCode: string) => {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(postOtpBegin());

    const otpToken = getState().login.otpToken;
    api.postOtp(otpCode, otpToken)
      .then(_response => {
        sessionStorage.removeItem('otpToken');
        dispatch(postOtpSuccess());

        const targetUrl = getState().login.targetUrl;
        const isDeepLink = targetUrl &&
          targetUrl !== ROUTES.AUTH &&
          !targetUrl.startsWith(ROUTES.TWO_FACTOR_AUTH);

        if (isDeepLink) {
          if (targetUrl.startsWith('/projects') || targetUrl.startsWith('/error')) {
            history.replace(targetUrl);
          } else {
            window.location.replace(window.location.origin + '/#' + targetUrl);
          }
        } else {
          history.replace(ROUTES.PROJECTS);
        }
      })
      .catch(errorResponse => {
        if (errorResponse.code === 401) {
          // 401: StudioAPI already redirects to login, clear the token
          sessionStorage.removeItem('otpToken');
          dispatch(updateMessage(errorResponse.message, LogLevel.Error));
          setTimeout(() => {
            dispatch(clearMessage());
            logout();
          }, 2000); // brief delay so the message is visible before redirect
        } else {
          // 422: show the error message from the API response
          dispatch(updateMessage(errorResponse.message, LogLevel.Error));
        }
        dispatch(postOtpFailure(errorResponse));
      });
  };
};

export const fetchSession = () => {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {

    // Check if user data already exists in the model
    const state = getState();
    if (state.login.id !== -1) {
      return;
    }

    dispatch(fetchSessionBegin());

    api.fetchSession()
      .then(response => {
        dispatch(fetchSessionSuccess(response.data));
      },
      errorResponse => {
        dispatch(fetchSessionFailure(errorResponse));
      });
  }
};

export const deleteSession = () => {
  return (dispatch: GenericThunkDispatch, getState: GenericThunkGetState) => {
    dispatch(deleteSessionBegin());

    api.deleteSession()
        .then(response => {
          sessionStorage.removeItem('otpToken');
          logout();
          dispatch(deleteSessionSuccess());
        },
        errorResponse => {
          sessionStorage.removeItem('otpToken');
          logout();
          dispatch(deleteSessionFailure(errorResponse));
        })
  }
};
